import React from "react";
import {IWorkSpaceData} from '../redux/workSpaceSlice';
import CourseNonThumbnail from '../assets/images/course-non-thumbnail.svg';
import {AMPLIFY_CONFIG} from "../configs/aws-exports";
import {images} from "../constants/images";
import badges from "../constants/badge_catalog.json"
import {BADGES_LEVEL_ORDER} from "../constants/constants";

interface VectorCounts {
  [key: string]: number;
}

interface VectorRequirements {
  [key: string]: number;
}

const numberFormatter = new Intl.NumberFormat('en-US');

export const buildLockedBadgeProgressText = (
  badgeId: string,
  current_count: number,
  required_count: number,
  vector_counts?: VectorCounts,
  vector_requirements?: VectorRequirements
) => {
  const badge = badges.find((badge) => badge.badgeId === badgeId);
  if (!badge) {
    console.log('Badge not found !');
    return null;
  }

  // Special handling for multi-vector badges
  if ((badgeId === 'MULTI_VECTOR_APPRENTICE' || badgeId === 'MULTI_VECTOR_MASTER') && vector_counts && vector_requirements) {
    // Count how many vector types have met their requirements
    const vectorKeys = Object.keys(vector_requirements);
    const totalVectors = vectorKeys.length;
    const completedVectors = vectorKeys.filter(
      (key) => (vector_counts[key] ?? 0) >= (vector_requirements[key] ?? 1)
    ).length;

    const displayTotalVectors = (completedVectors === 0 && totalVectors === 0) ? 4 : totalVectors;
    return `${completedVectors}/${displayTotalVectors} ${badge.lockedProgressText ?? ''}`;
  }

  // Default required_count to 4 if both counts are 0 (for multi-vector badges with no data yet)
  // const displayRequiredCount = (current_count === 0 && required_count === 0) ? 4 : required_count;
  // return `${current_count}/${displayRequiredCount} ${badge.lockedProgressText ?? ''}`;
  return `${current_count}/${required_count} ${badge.lockedProgressText ?? ''}`;
};

export const getBadgeDescription = (
  badgeId: string,
  status: string
) => {
  const badge = badges.find((badge) => badge.badgeId === badgeId);
  if (!badge) {
    console.log('Badge not found !');
    return null;
  }
  return status === 'unlocked' ? badge.unlockedProgressText : badge.lockedDescription;
};

export const roundUpPercentage = (percentage: number) => {
  return Math.ceil(percentage)
} 

export const parseImgURL = (tp_thumbnail?: string, tp_id?: number) => {
  const baseURL = process.env.REACT_APP_PH2_THUMBNAIL_URL;
  let imgURL = CourseNonThumbnail;
  if (tp_thumbnail && tp_id) {
    imgURL = `${baseURL + '/image_course_management/training_package/thumbnail/' + tp_id + '/' + tp_thumbnail}`;
  }

  return imgURL;
};

export const parseImgURLBadge = (category?: number, badgeId?: string) => {
  // const baseURL = `https://static.learnerhub1.stg.phriendlyphishing.com`;
  const baseURL = `https://static.${window.location.hostname}`;
  let imgURL = CourseNonThumbnail;
  if (category && badgeId) {
    imgURL = `${baseURL}/badges/${category}/${badgeId}.png`;
  }
  return imgURL;
};

export const getCurrentCellStore = () => {
  let currentCellStore: IWorkSpaceData | undefined = undefined;
  const pareCell = localStorage.getItem('currentCell') || '';
  if (pareCell) {
    currentCellStore = JSON.parse(pareCell);
  }

  return currentCellStore;
};

export const getUserName = (cellStore: IWorkSpaceData | undefined) => {
  if (cellStore) {
    return cellStore?.first_name?.trim() + ' ' + cellStore?.last_name?.trim();
  }
  return '';
};

interface IDataTab {
  label: string;
  component: React.ReactNode;
  hidden?: boolean;
}

export const checkManagerTabs = (data: IDataTab[], tabIndex: number) => {
  let result: IDataTab[] = [];
  let tabNumber = 0;
  if (data.length > 0) {
    result = data.filter((x) => !x.hidden);
  }
  if (result.length > tabIndex) {
    tabNumber = tabIndex;
  }

  return {key: result.length, data: result, defaultValue: tabNumber};
};

export const handleStartCourse = (token_1: string, token_2: string) => {
  const baseURL = process.env.REACT_APP_PH2_TRAINING_URL;
  const URL = `${baseURL}?t=${token_1}&p=${token_2}`;
  window.open(URL, '_blank', 'noreferrer');
};

export const getCertHandler = async (
  dispatch: any,
  getCertificateEvents: any,
  phishId: number | undefined,
  e: any
) => {
  e.preventDefault();
  const result = await dispatch(getCertificateEvents(phishId));
  const {payload} = result;
  if (payload && payload.file_data && payload.file_name) {
    downloadBase64File(payload.file_data, payload.file_name);
  }
};

export const downloadBase64File = (base64Data: string, fileName: string) => {
  const linkSource = `data:application/pdf;base64,${base64Data}`;
  const downloadLink = document.createElement('a');
  downloadLink.href = linkSource;
  downloadLink.download = fileName;
  downloadLink.click();
};

export const sanitizeHTML = (html = '') => {
  const formattedText = html.replace(/\r\n|\r|\n/g, "<br />");
  // remove <script> and text inside it
  let sanitizedHTML = formattedText.replace(/<script[^>]*>([\s\S]*?)<\/script>/gi, '');

  // remove <style> and text inside it
  sanitizedHTML = sanitizedHTML.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, '');
  return sanitizedHTML;
};

export const configAmplifyParams = () => {
  const isAuthSSO = localStorage.getItem('amplify-redirected-from-hosted-ui') || '';
  const oauthState = sessionStorage.getItem('oauth_state') || '';
  const clientId = localStorage.getItem('client_id') || '';

  if (isAuthSSO || oauthState) {
    AMPLIFY_CONFIG.Auth.userPoolWebClientId = clientId;
  } else {
    AMPLIFY_CONFIG.Auth.userPoolWebClientId = process.env.REACT_APP_AMPLIFY_APP_CLIENT_ID;
  }
  return AMPLIFY_CONFIG;
};
export const handleErrorImg = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  e.currentTarget.src = images.CourseNonThumbnail;
};

export const sortBadges = (badges: any[]) => {
    return [...badges].sort((firstBadge, secondBadge) => {
        const firstBadgeLevel = BADGES_LEVEL_ORDER.indexOf(firstBadge.rarity);
        const secondBadgeLevel = BADGES_LEVEL_ORDER.indexOf(secondBadge.rarity);

        if (firstBadgeLevel === secondBadgeLevel) {
            // same level -> sort by achieved date (latest to oldest)
            if (firstBadge.unlocked_at && secondBadge.unlocked_at) {
                return (
                    new Date(secondBadge.unlocked_at).getTime() - new Date(firstBadge.unlocked_at).getTime()
                );
            }
            return 0; // no unlocked_at date, treat them as equal
        }
        return firstBadgeLevel - secondBadgeLevel;
    });
};

export const formatNumber = (
  value?: string | number | null,
  fallback = ''
): string => {
  if (value == null || value === '') return fallback;

  const parsedNumber = Number(value);

  if (!Number.isFinite(parsedNumber)) return fallback;

  return numberFormatter.format(parsedNumber);
};